import React, { useState } from 'react';
import { BonusStatusTimelineEvent, BonusStatusTimelineEventType } from '../../providers/types';
import { formatDistanceToNow, format } from 'date-fns';

interface BonusStatusTimelineEventCardProps {
    event: BonusStatusTimelineEvent;
    isLast: boolean;
}

export const BonusStatusTimelineEventCard: React.FC<BonusStatusTimelineEventCardProps> = ({ event, isLast }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const isStatusChange = event.eventType === BonusStatusTimelineEventType.StatusChange;
    const eventDate = event.eventTimestamp;

    // Determine status color
    const getStatusColor = (status?: string): string => {
        if (!status) return 'gray';

        const statusLower = status.toLowerCase();
        if (statusLower.includes('approved') || statusLower.includes('opsapproved') || statusLower.includes('inpayrollprocessing') || statusLower.includes('paid')) return 'green';
        if (statusLower.includes('pending') || statusLower.includes('review') || statusLower.includes('pmreview') || statusLower.includes('inopsreview')) return 'orange';
        if (statusLower.includes('rejected') || statusLower.includes('issue') || statusLower.includes('busted')) return 'red';
        if (statusLower.includes('inuploadprocess') || statusLower.includes('notstarted') || statusLower.includes('draft')) return 'blue';
        return 'gray';
    };

    const statusColor = getStatusColor(event.currentStatus);

    // Truncate long comments
    const shouldTruncate = event.commentText && event.commentText.length > 200;
    const displayText = shouldTruncate && !isExpanded
        ? event.commentText!.substring(0, 200) + '...'
        : event.commentText;

    return (
        <div className={`timeline-event ${isStatusChange ? 'status-change' : 'comment'}`}>
            {/* Timeline connector */}
            <div className="timeline-connector">
                <div className={`timeline-dot ${isStatusChange ? 'filled' : 'hollow'} ${statusColor}`}>
                    {isStatusChange ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
                            <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                        </svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                        </svg>
                    )}
                </div>
                {!isLast && <div className={`timeline-line ${isStatusChange ? 'bold' : 'thin'}`} />}
            </div>

            {/* Event content */}
            <div className="event-content">
                <div className="event-header">
                    <div className="event-title">
                        {isStatusChange ? (
                            <span className="status-change-title">
                                <strong>{event.currentStatus}</strong>
                                {event.bonusTypeName && (
                                    <span className="bonus-type-badge">{event.bonusTypeName}</span>
                                )}
                            </span>
                        ) : (
                            <span className="comment-title">
                                <strong>Comment added</strong>
                                {event.action && (
                                    <span className="action-badge">{event.action}</span>
                                )}
                            </span>
                        )}
                    </div>

                    <div className="event-meta">
                        <div className="event-date-container">
                            <span className="event-date-relative" title={format(eventDate, 'MMMM d, yyyy h:mm:ss a')}>
                                {formatDistanceToNow(eventDate, { addSuffix: true })}
                            </span>
                            <span className="event-date-full">
                                {format(eventDate, 'MMM d, yyyy h:mm a')}
                            </span>
                        </div>
                        <span className="event-actor">
                            {event.updatedByUser}
                            {event.roleName && <span className="role-name">({event.roleName})</span>}
                        </span>
                    </div>
                </div>

                {isStatusChange && event.previousStatus && (
                    <div className="status-transition">
                        <span className="previous-status">{event.previousStatus}</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="current-status">{event.currentStatus}</span>
                    </div>
                )}

                {event.commentText && (
                    <div className="comment-content">
                        <p className={shouldTruncate && !isExpanded ? 'truncated' : ''}>
                            {displayText}
                        </p>
                        {shouldTruncate && (
                            <button
                                className="btn-expand"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                        )}
                    </div>
                )}

                {event.fileName && event.fileAttachmentId && (
                    <div className="file-attachment">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0V3z" />
                        </svg>

                        <a
                            href={`/api/cba/file-attachments/${event.fileAttachmentId}`}
                            className="attachment-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {event.fileName}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};